export type PayrollVisitScheduleMeta = {
  visitCount?: number;
  visits?: unknown[];
};

export function hasMultiplePayrollDayVisits(
  schedule?: PayrollVisitScheduleMeta | null
): boolean {
  if (!schedule) return false;
  return (
    Number(schedule.visitCount) > 1 ||
    (Array.isArray(schedule.visits) && schedule.visits.length > 1)
  );
}

/** Horas extra cuando el día suma más de una jornada (varios cortes / sucursales). */
export function multiVisitDayExtraHours(
  totalHours: number,
  fullDayHours: number
): number {
  const total = Number(totalHours);
  if (!Number.isFinite(total) || total <= fullDayHours) {
    return 0;
  }
  return Math.floor(total - fullDayHours);
}

export function alreadyIncludedExtraHoursInPayment(
  hoursUsedForCalc: number,
  fullDayHours: number,
  roundHoursFn?: (hours: number, fullDayHours: number) => number
): number {
  const rounded = roundHoursFn
    ? roundHoursFn(hoursUsedForCalc, fullDayHours)
    : hoursUsedForCalc;
  return Math.max(0, Math.floor(rounded - fullDayHours));
}

/**
 * Horas extra adicionales a pagar cuando hay varios cortes el mismo día
 * (p. ej. mañana en otra sucursal + turno nocturno con jornada completa).
 */
export function additionalMultiVisitExtraPay(
  totalHours: number,
  hoursInSchedule: number,
  fullDayHours: number,
  NormalCalculationNomina: boolean,
  schedule?: PayrollVisitScheduleMeta | null,
  roundHoursFn?: (hours: number, fullDayHours: number) => number
): number {
  if (!hasMultiplePayrollDayVisits(schedule)) {
    return 0;
  }
  const multiExtra = multiVisitDayExtraHours(totalHours, fullDayHours);
  if (multiExtra <= 0) {
    return 0;
  }
  const hoursUsedForCalc = NormalCalculationNomina
    ? totalHours
    : hoursInSchedule > 0
      ? hoursInSchedule
      : totalHours;
  const alreadyIncluded = alreadyIncludedExtraHoursInPayment(
    hoursUsedForCalc,
    fullDayHours,
    roundHoursFn
  );
  return Math.max(0, multiExtra - alreadyIncluded);
}

export function appendMultiVisitExtrasToConcept(
  concept: string,
  totalHours: number,
  fullDayHours: number,
  schedule?: PayrollVisitScheduleMeta | null,
  NormalCalculationNomina = false
): string {
  if (!hasMultiplePayrollDayVisits(schedule) || concept.includes("extras")) {
    return concept;
  }
  const extra = multiVisitDayExtraHours(totalHours, fullDayHours);
  if (extra <= 0) {
    return concept;
  }
  const suffix = NormalCalculationNomina ? " " : "";
  if (concept.includes("jornada completa por salida nocturna")) {
    return concept.replace(
      "jornada completa por salida nocturna)",
      `jornada completa por salida nocturna) + ${extra}h extras${suffix}`
    );
  }
  if (concept.includes("(jornada completa)")) {
    return concept.replace(
      "(jornada completa)",
      `(jornada completa + ${extra}h extras)`
    );
  }
  return `${concept} + ${extra}h extras${suffix}`;
}
